import React, { useEffect, useState } from 'react'
import {
  CRow, CCol, CFormInput, CFormLabel, CFormSelect, CButton, CSpinner, CAlert, CCard, CCardBody, CCardHeader,
  CTabs, CTabList, CTab, CTabContent, CTabPanel, CTable,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilPrint, cilSearch, cilMoney, cilCheckCircle, cilWarning } from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { hoyLocal } from '../utils/fecha'
import { fmtMoneda } from '../utils/moneda'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'


/// INFORME DE MOVIMIENTOS (informes.cs:404 del GX — "INFORME MOVIMIENTOS
/// RUTA SEGUN FECHA Y RUTA"): ruta + fechas → Generar → Créditos/Abonos/Atrasos
/// en pantalla (WwGrid) + Descargar PDF. Diseño CoreUI del proyecto.
const ReportesPage: React.FC = () => {
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [tabActiva, setTabActiva] = useState('movimientos')
  const [rutas, setRutas] = useState<any[]>([])
  const [zona, setZona] = useState('')
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [informe, setInforme] = useState<any>(null)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState('')

  // Matriz semanal (informessemanal.cs — ruta + día de inicio).
  const [semZona, setSemZona] = useState('')
  const [semDesde, setSemDesde] = useState('')
  const [semana, setSemana] = useState<any>(null)
  const [semCargando, setSemCargando] = useState(false)
  const [semError, setSemError] = useState('')

  const generarSemanal = () => {
    setSemError('')
    if (!semZona || !semDesde) {
      setSemError('Seleccione la ruta y el dia de inicio de la semana.')
      return
    }
    setSemCargando(true)
    api
      .get(`/reportes/matriz-semanal?empresa=${empresa}&zona=${semZona}&desde=${semDesde}`)
      .then(setSemana)
      .catch((e) => setSemError(e.message))
      .finally(() => setSemCargando(false))
  }

  // [RGM] 2026-10-03 (pedido Rafael): abre en pestaña nueva vía api.abrirPdf
  // (antes descargaba con blob + anchor).
  const descargarSemanalPdf = async () => {
    try {
      await api.abrirPdf(
        `/reportes/matriz-semanal.pdf?empresa=${empresa}&zona=${semZona}&desde=${semDesde}`,
      )
    } catch (e: any) {
      setSemError(e.message ?? 'No se pudo abrir la matriz')
    }
  }

  useEffect(() => {
    api.get<any[]>(`/rutas?empresa=${empresa}`).then(setRutas).catch(() => setRutas([]))
  }, [empresa])

  const generar = () => {
    setError('')
    if (!zona || !desde || !hasta) {
      setError('Seleccione la ruta y las fechas del informe.')
      return
    }
    setCargando(true)
    api
      .get(`/reportes/movimientos?empresa=${empresa}&zona=${zona}&desde=${desde}&hasta=${hasta}`)
      .then((d) => setInforme(d))
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false))
  }

  const descargarPdf = async () => {
    try {
      await api.abrirPdf(
        `/reportes/movimientos.pdf?empresa=${empresa}&zona=${zona}&desde=${desde}&hasta=${hasta}`,
      )
    } catch (e: any) {
      setError(e.message ?? 'No se pudo abrir el informe')
    }
  }

  const fecha = (iso?: string) => (iso ? String(iso).slice(0, 10) : '')

  const colCreditos: WwColumn[] = [
    { key: 'prestamoNro', title: 'Prestamo Nro.', type: 'number' },
    { key: 'fecha', title: 'Fecha', type: 'date' },
    { key: 'valor', title: 'Valor', type: 'currency' },
    { key: 'clienteNombre', title: 'Nombre del Cliente' },
    { key: 'cobrador', title: 'Nombre Cobrador' },
  ]
  const colAbonos: WwColumn[] = [
    { key: 'abonoNro', title: 'Abono Nro.', type: 'number' },
    { key: 'fecha', title: 'Fecha', type: 'date' },
    { key: 'hora', title: 'Hora' },
    { key: 'valor', title: 'Valor', type: 'currency' },
    { key: 'clienteNombre', title: 'Nombre del Cliente' },
    { key: 'prestamoNro', title: 'Prestamo Nro.', type: 'number' },
    { key: 'cobrador', title: 'Nombre Cobrador' },
  ]
  const colAtrasos: WwColumn[] = [
    { key: 'atrazoNro', title: 'Atraso Nro.', type: 'number' },
    { key: 'fecha', title: 'Fecha', type: 'date' },
    { key: 'valorCondenado', title: 'Valor Condenado', type: 'currency' },
    { key: 'clienteNombre', title: 'Nombre del Cliente' },
    { key: 'prestamoNro', title: 'Prestamo Nro.', type: 'number' },
    { key: 'cobrador', title: 'Nombre Cobrador' },
  ]

  return (
    <div>
      <h4>Informes</h4>
      <CTabs activeItemKey={tabActiva} onChange={(k) => setTabActiva(String(k))} className="mb-3">
        <CTabList variant="tabs">
          <CTab itemKey="movimientos">Movimientos</CTab>
          <CTab itemKey="semanal">Semanal</CTab>
          <CTab itemKey="morosos">Morosos</CTab>
          <CTab itemKey="condenas">Condenas</CTab>
          <CTab itemKey="pasados">Pasados</CTab>
          <CTab itemKey="inactivos">Inactivos</CTab>
          <CTab itemKey="carteras">Carteras</CTab>
          <CTab itemKey="utilidades">Utilidades</CTab>
          <CTab itemKey="corte">Corte</CTab>
        </CTabList>
        <CTabContent>
          <CTabPanel itemKey="movimientos" className="pt-3">
      <CCard className="mb-3 shadow-sm">
        <CCardHeader className="fw-semibold">
          INFORME MOVIMIENTOS RUTA SEGUN FECHA Y RUTA
        </CCardHeader>
        <CCardBody>
          <CRow className="g-3 align-items-end">
            <CCol md={3}>
              <CFormLabel>Ruta</CFormLabel>
              <CFormSelect value={zona} onChange={(e) => setZona(e.target.value)}>
                <option value="">Seleccione la ruta...</option>
                {rutas.map((r: any) => (
                  <option key={r.zonaCodigo} value={r.zonaCodigo}>
                    {String(r.zonaNombre).trim()}
                  </option>
                ))}
              </CFormSelect>
            </CCol>
            <CCol md={3}>
              <CFormLabel>Fecha Inicial</CFormLabel>
              <CFormInput type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)} />
            </CCol>
            <CCol md={3}>
              <CFormLabel>Fecha Final</CFormLabel>
              <CFormInput type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} />
            </CCol>
            <CCol md={3} className="d-flex gap-2">
              <CButton color="primary" onClick={generar} disabled={cargando}>
                {cargando ? <CSpinner size="sm" /> : <CIcon icon={cilSearch} className="me-1" />}
                Generar Informe
              </CButton>
              {informe && (
                <CButton color="success" onClick={descargarPdf}>
                  <CIcon icon={cilPrint} className="me-1" />
                  Descargar PDF
                </CButton>
              )}
            </CCol>
          </CRow>
          {error && <CAlert color="danger" className="mt-3 mb-0">{error}</CAlert>}
        </CCardBody>
      </CCard>

      {informe && (
        <>
          <div className="info-resumen-grid">
            <ResumenCard clase="info-creditos" icono={cilMoney} etiqueta="Prestamos"
              valor={fmtMoneda(informe.totalCreditos)}
              detalle={`${(informe.creditos ?? []).length} prestamos en el periodo`} />
            <ResumenCard clase="info-abonos" icono={cilCheckCircle} etiqueta="Abonos"
              valor={fmtMoneda(informe.totalAbonos)}
              detalle={`${(informe.abonos ?? []).length} abonos en el periodo`} />
            <ResumenCard clase="info-atrasos" icono={cilWarning} etiqueta="Atrasos"
              valor={fmtMoneda(informe.totalAtrasos)}
              detalle={`${(informe.atrasos ?? []).length} atrasos en el periodo`} />
          </div>

          <SeccionInforme
            color="info"
            titulo="Prestamos"
            total={informe.totalCreditos}
            columnas={colCreditos}
            filas={(informe.creditos ?? []).map((f: any) => ({
              ...f,
              fecha: fecha(f.fecha),
              clienteNombre: String(f.clienteNombre ?? '').trim(),
            }))}
          />
          <SeccionInforme
            color="success"
            titulo="Abonos"
            total={informe.totalAbonos}
            columnas={colAbonos}
            filas={(informe.abonos ?? []).map((f: any) => ({
              ...f,
              fecha: fecha(f.fecha),
              clienteNombre: String(f.clienteNombre ?? '').trim(),
            }))}
          />
          <SeccionInforme
            color="warning"
            titulo="Atrasos"
            total={informe.totalAtrasos}
            columnas={colAtrasos}
            filas={(informe.atrasos ?? []).map((f: any) => ({
              ...f,
              fecha: fecha(f.fecha),
              clienteNombre: String(f.clienteNombre ?? '').trim(),
            }))}
          />
        </>
      )}
          </CTabPanel>
          <CTabPanel itemKey="semanal" className="pt-3">
            <CCard className="mb-3 shadow-sm">
              <CCardHeader className="fw-semibold">
                MATRIZ ABONOS SEMANAL POR RUTA
              </CCardHeader>
              <CCardBody>
                <CRow className="g-3 align-items-end">
                  <CCol md={4}>
                    <CFormLabel>Ruta</CFormLabel>
                    <CFormSelect value={semZona} onChange={(e) => setSemZona(e.target.value)}>
                      <option value="">Seleccione la ruta...</option>
                      {rutas.map((r: any) => (
                        <option key={r.zonaCodigo} value={r.zonaCodigo}>
                          {String(r.zonaNombre).trim()}
                        </option>
                      ))}
                    </CFormSelect>
                  </CCol>
                  <CCol md={4}>
                    <CFormLabel>Dia de Inicio (semana)</CFormLabel>
                    <CFormInput type="date" value={semDesde} onChange={(e) => setSemDesde(e.target.value)} />
                  </CCol>
                  <CCol md={4} className="d-flex gap-2">
                    <CButton color="primary" onClick={generarSemanal} disabled={semCargando}>
                      {semCargando ? <CSpinner size="sm" /> : <CIcon icon={cilSearch} className="me-1" />}
                      Generar Informe
                    </CButton>
                    {semana && (
                      <CButton color="success" onClick={descargarSemanalPdf}>
                        <CIcon icon={cilPrint} className="me-1" />
                        Descargar PDF
                      </CButton>
                    )}
                  </CCol>
                </CRow>
                {semError && <CAlert color="danger" className="mt-3 mb-0">{semError}</CAlert>}
              </CCardBody>
            </CCard>

            {semana && (
              <>
                <div className="info-resumen-grid">
                  <ResumenCard clase="info-abonos" icono={cilMoney} etiqueta="Total Semana"
                    valor={fmtMoneda(semana.totalSemana)}
                    detalle={`${semana.filas.length} clientes abonaron`} />
                  <ResumenCard clase="info-creditos" icono={cilSearch} etiqueta="Ruta"
                    valor={String(semana.ruta ?? '').trim()} detalle={semana.desde ? `Semana: ${String(semana.desde).slice(0, 10)}` : ''} />
                  <ResumenCard clase="info-atrasos" icono={cilCheckCircle} etiqueta="Dias"
                    valor="7" detalle="Lunes a Domingo" />
                </div>

                <CCard className="shadow-sm border-0" style={{ overflow: 'hidden' }}>
                  <CCardHeader className="fw-semibold text-uppercase">
                    Matriz de Abonos por Cliente y Dia
                  </CCardHeader>
                  <CCardBody>
                    {semana.filas.length === 0 ? (
                      <div className="text-center text-secondary py-3">No hay abonos en la semana.</div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <CTable hover responsive striped>
                          <thead>
                            <tr>
                              <th>Cliente</th>
                              {semana.dias.map((d: string, i: number) => (
                                <th key={i} className="text-end">{fecha(d).slice(5)}</th>
                              ))}
                              <th className="text-end">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {semana.filas.map((f: any, i: number) => (
                              <tr key={i}>
                                <td>{String(f.clienteNombre ?? '').trim()}</td>
                                {f.abonos.map((v: number, j: number) => (
                                  <td key={j} className="text-end">{fmtMoneda(v)}</td>
                                ))}
                                <td className="text-end fw-bold">{fmtMoneda(f.total)}</td>
                              </tr>
                            ))}
                            <tr className="fw-bold">
                              <td>Totales</td>
                              {semana.totalesPorDia.map((v: number, j: number) => (
                                <td key={j} className="text-end">{fmtMoneda(v)}</td>
                              ))}
                              <td className="text-end">{fmtMoneda(semana.totalSemana)}</td>
                            </tr>
                          </tbody>
                        </CTable>
                      </div>
                    )}
                  </CCardBody>
                </CCard>
              </>
            )}
          </CTabPanel>
          <CTabPanel itemKey="morosos" className="pt-3">
            {/* PRESTAMOS MOROSOS — columnas EXACTAS del GX
                (arptprestamosmorosos.cs:206-219, orden físico por posición X):
                No., Valor, Cliente, Cedula, Teléfono, Celular, Prestamo Total,
                Vlor.Abonos, Pagas, Pend., Mora, Saldo Prest., Saldo Condena,
                Saldo Real. (NO lleva MPago, Uti. Actual ni Fecha/Termina —
                eso es de Carteras.) */}
            <GestionReporte titulo="PRESTAMOS MOROSOS" ruta="prestamos-morosos"
              columnas={['prestamoNro', 'prestamoValor', 'clienteNombre', 'clienteCedula',
                'clienteTelefono', 'clienteCelular', 'prestamoTotal', 'abonosTotal',
                'cuotasAbonadas', 'cuotasPendientes', 'prestamoDiasMora', 'prestamoSaldo',
                'saldoCondenas', 'saldoReal']}
              etiquetas={['No.', 'Valor', 'Cliente', 'Cedula', 'Teléfono', 'Celular',
                'Prestamo Total', 'Vlor.Abonos', 'Pagas', 'Pend.', 'Mora', 'Saldo Prest.',
                'Saldo Condena', 'Saldo Real']} empresa={empresa}
              // [RGM] 2026-10-10 (pedido Rafael): MOROSOS es el reporte donde
              // se ELIGEN los días de mora (param `diasMinimos` del endpoint).
              filtroLabel="Dias de mora minimos" filtroDefault="1" filtroParam="diasMinimos" />
          </CTabPanel>
          <CTabPanel itemKey="condenas" className="pt-3">
            <GestionReporte titulo="INFORME CONDENAS POR CLIENTE" ruta="condenas-por-cliente"
              columnas={['prestamoNro', 'clienteCedula', 'prestamoAtrazosCondenados', 'prestamoValorCondena']}
              etiquetas={['Prestamo Nro.', 'Cedula', 'Condenados', 'Valor Condena']} empresa={empresa} />
          </CTabPanel>
          <CTabPanel itemKey="pasados" className="pt-3">
            <GestionReporte titulo="PRESTAMOS PASADOS DE FECHA" ruta="pasados-de-fecha"
              columnas={['prestamoNro', 'clienteCedula', 'prestamoSaldo', 'prestamoFechaTermina']}
              etiquetas={['Prestamo Nro.', 'Cedula', 'Saldo', 'Fecha Termina']} empresa={empresa} />
          </CTabPanel>
          <CTabPanel itemKey="inactivos" className="pt-3">
            <GestionReporte titulo="PRESTAMOS CLIENTES INACTIVOS" ruta="clientes-inactivos"
              columnas={['prestamoNro', 'clienteCedula', 'prestamoSaldo']}
              etiquetas={['Prestamo Nro.', 'Cedula', 'Saldo']} empresa={empresa} />
          </CTabPanel>
          <CTabPanel itemKey="utilidades" className="pt-3">
            <UtilidadReporte empresa={empresa} />
          </CTabPanel>
          <CTabPanel itemKey="corte" className="pt-3">
            <CorteTab empresa={empresa} />
          </CTabPanel>
          <CTabPanel itemKey="carteras" className="pt-3">
            {/* ESTADO DE CARTERAS — columnas EXACTAS del GX
                (arptestadocarteras.cs:209-232, orden físico por posición X):
                No., Valor, Fecha, Termina, Cliente, Cedula, Teléfono, Celular,
                MPago, Prestamo Total, Vlor.Abonos, Uti. Actual, Pagas, Pend.,
                Mora, Sdo Prest., Sdo Condena, Saldo Real. */}
            <GestionReporte titulo="ESTADO DE CARTERAS" ruta="estado-carteras"
              columnas={['prestamoNro', 'prestamoValor', 'prestamoFecha', 'prestamoFechaTermina',
                'clienteNombre', 'clienteCedula', 'clienteTelefono', 'clienteCelular',
                'prestamoMPago', 'prestamoTotal', 'abonosTotal', 'prestamoUtilidadReal',
                'cuotasAbonadas', 'cuotasPendientes', 'prestamoDiasMora', 'prestamoSaldo',
                'saldoCondenas', 'saldoReal']}
              etiquetas={['No.', 'Valor', 'Fecha', 'Termina', 'Cliente', 'Cedula', 'Teléfono',
                'Celular', 'MPago', 'Prestamo Total', 'Vlor.Abonos', 'Uti. Actual',
                'Pagas', 'Pend.', 'Mora', 'Sdo Prest.', 'Sdo Condena', 'Saldo Real']}
              empresa={empresa}
              // [RGM] 2026-10-10 (pedido Rafael): CARTERAS = TODA la cartera
              // (sin selector; días fijos 0 → todo préstamo con saldo real).
              diasFijos={0} />
          </CTabPanel>
        </CTabContent>
      </CTabs>
    </div>
  )
}

/// INFORME DE UTILIDAD SEGUN FECHA (informeutilidadporfecha.cs): filtros de
/// fechas → utilidad por día (SUM AbonoLibreValor1) + TOTAL + Descargar PDF.
function UtilidadReporte({ empresa }: { empresa: string }) {
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [data, setData] = useState<any[] | null>(null)
  const [error, setError] = useState('')
  const [cargando, setCargando] = useState(false)

  const cargar = () => {
    setError('')
    if (!desde || !hasta) {
      setError('Seleccione las fechas del informe.')
      return
    }
    setCargando(true)
    api
      .get<any[]>(`/reportes/utilidad-por-fecha?empresa=${empresa}&desde=${desde}&hasta=${hasta}`)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false))
  }

  const descargar = async () => {
    try {
      await api.abrirPdf(
        `/reportes/utilidad-por-fecha.pdf?empresa=${empresa}&desde=${desde}&hasta=${hasta}`,
      )
    } catch (e: any) {
      setError(e.message ?? 'No se pudo abrir')
    }
  }

  const dias = (data ?? []).filter((d) => d.fecha)
  const total = (data ?? []).find((d) => !d.fecha)?.utilidad ?? 0
  const cols: WwColumn[] = [
    { key: 'fecha', title: 'Fecha', type: 'date' },
    { key: 'utilidad', title: 'Utilidad', type: 'currency' },
  ]

  return (
    <CCard className="mb-3 shadow-sm border-0" style={{ overflow: 'hidden' }}>
      {/* Encabezado sobrio UNIFICADO (patrón Movimientos/Semanal): sin franja
          azul — el azul queda como acento en tabs y botones. */}
      <CCardHeader className="fw-semibold text-uppercase">Informe de Utilidad segun Fecha</CCardHeader>
      <CCardBody>
        {/* Filtros en el BODY + botones tamaño normal (patrón aprobado de
            Movimientos/Semanal): el GX llama el botón "Generar Informe"
            (informeutilidadporfecha.cs:459). */}
        <CRow className="g-3 align-items-end mb-3">
          <CCol md={4}>
            <CFormLabel>Fecha Inicial</CFormLabel>
            <CFormInput type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)} />
          </CCol>
          <CCol md={4}>
            <CFormLabel>Fecha Final</CFormLabel>
            <CFormInput type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} />
          </CCol>
          <CCol md={4} className="d-flex gap-2">
            <CButton color="primary" onClick={cargar} disabled={cargando}>
              {cargando ? <CSpinner size="sm" /> : <CIcon icon={cilSearch} className="me-1" />}
              Generar Informe
            </CButton>
            {data && (
              <CButton color="success" onClick={descargar}>
                <CIcon icon={cilPrint} className="me-1" />
                Descargar PDF
              </CButton>
            )}
          </CCol>
        </CRow>
        {error && <CAlert color="danger" className="mb-3">{error}</CAlert>}
        {data === null ? (
          <div className="text-center text-secondary py-3">Pulse "Generar Informe" para ver el informe.</div>
        ) : dias.length === 0 ? (
          <div className="text-center text-secondary py-3">No hay abonos en el período.</div>
        ) : (
          <>
            <div className="info-resumen-grid mb-3">
              <ResumenCard clase="info-creditos" icono={cilMoney} etiqueta="Utilidad Total"
                valor={fmtMoneda(total)}
                detalle={`${dias.length} dias con abonos`} />
            </div>
            <WwGrid title="Utilidad por Fecha" columns={cols} rows={dias} onTotalChange={() => undefined} />
          </>
        )}
      </CCardBody>
    </CCard>
  )
}

/// Reporte de gestión del menú Informes del GX (MenuOptionsDataAdmin.cs:239-308):
/// Morosos → listarprestamosruta.cs, Condenas → arptinformecondenasporcliente.cs,
/// Pasados → arptpasadosdefecha.cs, Inactivos → arptprestamosclientesinactivos.cs,
/// Carteras → arptestadocarteras.cs. Carga los datos + grilla + Descargar PDF
/// (patrón del informe de movimientos — diseño CoreUI [RGM] aprobado).
function GestionReporte({
  titulo,
  ruta,
  columnas,
  etiquetas,
  empresa,
  filtroLabel,
  filtroDefault,
  filtroParam,
  diasFijos,
}: {
  titulo: string
  ruta: string
  columnas: string[]
  etiquetas: string[]
  empresa: string
  filtroLabel?: string
  filtroDefault?: string
  /** Nombre del query param del filtro de días (default 'dias'; morosos usa 'diasMinimos'). */
  filtroParam?: string
  /** Días FIJOS para reportes sin input (Carteras = toda la cartera: 0). */
  diasFijos?: number
}) {
  const [data, setData] = useState<any[] | null>(null)
  const [error, setError] = useState('')
  const [cargando, setCargando] = useState(false)
  const [filtro, setFiltro] = useState(filtroDefault ?? '')

  // Filtro de días → query param del reporte: Morosos ELIGE días
  // (`diasMinimos`); Carteras sin input = TODA la cartera (`dias=0`); sin
  // filtro ni fijos → sin param (default del backend).
  const extraFiltro = () => {
    if (diasFijos !== undefined) return `&${filtroParam ?? 'dias'}=${diasFijos}`
    if (filtro) return `&${filtroParam ?? 'dias'}=${filtro}`
    return ''
  }

  const cargar = () => {
    setError('')
    setCargando(true)
    api
      .get<any[]>(`/reportes/${ruta}?empresa=${empresa}${extraFiltro()}`)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false))
  }

  const descargar = async () => {
    try {
      // [RGM] 2026-10-10 (bug reportado por Rafael: "el PDF de Carteras solo
      // muestra días de mora 1"): el PDF ahora recibe EL MISMO filtro que la
      // grilla — antes no se mandaba nada y el backend tenía los días fijos.
      await api.abrirPdf(`/reportes/${ruta}.pdf?empresa=${empresa}${extraFiltro()}`)
    } catch (e: any) {
      setError(e.message ?? 'No se pudo abrir')
    }
  }

  // Tipo por columna: dinero con las máscaras GX (Valor/Saldo/Total/Utilidad),
  // fechas del GX y contadores numéricos del ESTADO DE CARTERAS (MPago, Pagas,
  // Pend., Mora — arptestadocarteras.cs:324-332).
  const TIPOS_NUMERICOS: Record<string, WwColumn['type']> = {
    prestamoMPago: 'number',
    cuotasAbonadas: 'number',
    cuotasPendientes: 'number',
    prestamoDiasMora: 'number',
  }
  const cols: WwColumn[] = columnas.map((c, i) => ({
    key: c,
    title: etiquetas[i] ?? c,
    type: TIPOS_NUMERICOS[c]
      ?? (c.includes('Valor') || c.includes('Saldo') || c.includes('saldo')
        || c.includes('Total') || c.includes('Utilidad')
        ? 'currency'
        : c.includes('Fecha') ? 'date' : undefined),
  }))

  return (
    <CCard className="mb-3 shadow-sm border-0" style={{ overflow: 'hidden' }}>
      {/* Encabezado sobrio UNIFICADO (patrón Movimientos/Semanal): título
          solo — filtros y botones van en el body, el azul como acento. */}
      <CCardHeader className="fw-semibold text-uppercase">{titulo}</CCardHeader>
      <CCardBody>
        <CRow className="g-3 align-items-end mb-3">
          {filtroLabel && (
            <CCol md={3}>
              <CFormLabel>{filtroLabel}</CFormLabel>
              <CFormInput
                type="number"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
              />
            </CCol>
          )}
          <CCol md={3} className="d-flex gap-2">
            <CButton color="primary" onClick={cargar} disabled={cargando}>
              {cargando ? <CSpinner size="sm" /> : <CIcon icon={cilSearch} className="me-1" />}
              Cargar
            </CButton>
            {data && (
              <CButton color="success" onClick={descargar}>
                <CIcon icon={cilPrint} className="me-1" />
                Descargar PDF
              </CButton>
            )}
          </CCol>
        </CRow>
        {error && <CAlert color="danger" className="mb-3">{error}</CAlert>}
        {data === null ? (
          <div className="text-center text-secondary py-3">
            Pulse "Cargar" para ver el informe.
          </div>
        ) : data.length === 0 ? (
          <div className="text-center text-secondary py-3">No hay registros.</div>
        ) : (
          <>
            {/* Tarjetas de resumen con los totales del GX por ruta (solo
                cuando hay datos cargados — patrón de Movimientos). */}
            <div className="info-resumen-grid mb-3">
              <ResumenGestion ruta={ruta} filas={data} />
            </div>
            <WwGrid title={titulo} columns={cols} rows={data} onTotalChange={() => undefined} />
          </>
        )}
      </CCardBody>
    </CCard>
  )
}

/// Tarjetas de resumen de los reportes de gestión (diseño aprobado de
/// Movimientos/Semanal) con los totales por ruta del GX:
/// - prestamos-morosos: los acumuladores AV del "Totales:" de cada ruta —
///   AV11 (Prestamo Total), AV10 (Valor), AV9 (Abonos), AV14 (Saldos),
///   AV12 (Saldo Condenas) y AV13 (Saldo Real) — se imprimen en
///   arptprestamosmorosos.cs:330-336; AV15/AV16 se acumulan pero el GX no los
///   imprime (no se reflejan).
/// - condenas-por-cliente: "Cantidad Condenas:" (arptinformecondenasporcliente.cs:298)
///   y "Valor Total Condenas:" (:300) — el valor es AtrazosCondenados ×
///   ValorCondena por fila.
/// - pasados-de-fecha: AV10TotalPrestamos, AV14TotalSaldos y AV13TotalSaldoReal
///   (arptpasadosdefecha.cs:325-331).
/// - clientes-inactivos: AV13TotalSaldos (arptprestamosclientesinactivos.cs:341-347).
/// - estado-carteras: Cantidad + AV15TotalSaldos + AV13TotalSaldoCondenas +
///   AV14TotalSaldoReal (arptestadocarteras.cs:362-374, filas del informe).
function ResumenGestion({ ruta, filas }: { ruta: string; filas: any[] }) {
  const n = filas.length
  const suma = (key: string) => filas.reduce((acc: number, f: any) => acc + Number(f[key] ?? 0), 0)

  if (ruta === 'prestamos-morosos') {
    return (
      <>
        <ResumenCard clase="info-creditos" icono={cilMoney} etiqueta="Prestamos Morosos"
          valor={String(n)} detalle={`${n} registros`} />
        <ResumenCard clase="info-abonos" icono={cilCheckCircle} etiqueta="Total Prestado"
          valor={fmtMoneda(suma('prestamoValor'))} detalle={`de ${n} prestamos`} />
        <ResumenCard clase="info-creditos" icono={cilMoney} etiqueta="Prestamo Total"
          valor={fmtMoneda(suma('prestamoTotal'))} detalle={`de ${n} prestamos`} />
        <ResumenCard clase="info-abonos" icono={cilCheckCircle} etiqueta="Total Abonos"
          valor={fmtMoneda(suma('abonosTotal'))} detalle={`de ${n} prestamos`} />
        <ResumenCard clase="info-atrasos" icono={cilWarning} etiqueta="Total Saldos"
          valor={fmtMoneda(suma('prestamoSaldo'))} detalle={`de ${n} prestamos`} />
        <ResumenCard clase="info-atrasos" icono={cilWarning} etiqueta="Saldo Condenas"
          valor={fmtMoneda(suma('saldoCondenas'))} detalle={`de ${n} prestamos`} />
        <ResumenCard clase="info-creditos" icono={cilCheckCircle} etiqueta="Saldo Real"
          valor={fmtMoneda(suma('saldoReal'))} detalle={`de ${n} prestamos`} />
      </>
    )
  }
  if (ruta === 'condenas-por-cliente') {
    const valorTotal = filas.reduce(
      (acc: number, f: any) => acc + Number(f.prestamoAtrazosCondenados ?? 0) * Number(f.prestamoValorCondena ?? 0), 0)
    return (
      <>
        <ResumenCard clase="info-creditos" icono={cilMoney} etiqueta="Prestamos con Condenas"
          valor={String(n)} detalle={`${n} registros`} />
        <ResumenCard clase="info-abonos" icono={cilCheckCircle} etiqueta="Cantidad Condenas"
          valor={String(suma('prestamoAtrazosCondenados'))} detalle={`de ${n} prestamos`} />
        <ResumenCard clase="info-atrasos" icono={cilWarning} etiqueta="Valor Total Condenas"
          valor={fmtMoneda(valorTotal)} detalle={`de ${n} prestamos`} />
      </>
    )
  }
  if (ruta === 'pasados-de-fecha') {
    return (
      <>
        <ResumenCard clase="info-creditos" icono={cilMoney} etiqueta="Prestamos Pasados"
          valor={String(n)} detalle={`${n} registros`} />
        <ResumenCard clase="info-abonos" icono={cilCheckCircle} etiqueta="Total Saldos"
          valor={fmtMoneda(suma('prestamoSaldo'))} detalle={`de ${n} prestamos`} />
        <ResumenCard clase="info-atrasos" icono={cilWarning} etiqueta="Saldo Real"
          valor={fmtMoneda(suma('saldoReal'))} detalle={`de ${n} prestamos`} />
      </>
    )
  }
  if (ruta === 'clientes-inactivos') {
    return (
      <>
        <ResumenCard clase="info-creditos" icono={cilMoney} etiqueta="Prestamos Inactivos"
          valor={String(n)} detalle={`${n} registros`} />
        <ResumenCard clase="info-abonos" icono={cilCheckCircle} etiqueta="Total Saldos"
          valor={fmtMoneda(suma('prestamoSaldo'))} detalle={`de ${n} prestamos`} />
      </>
    )
  }
  if (ruta === 'estado-carteras') {
    return (
      <>
        <ResumenCard clase="info-creditos" icono={cilMoney} etiqueta="Prestamos en Cartera"
          valor={String(n)} detalle={`${n} registros`} />
        <ResumenCard clase="info-abonos" icono={cilCheckCircle} etiqueta="Total Saldos"
          valor={fmtMoneda(suma('prestamoSaldo'))} detalle={`de ${n} prestamos`} />
        <ResumenCard clase="info-atrasos" icono={cilWarning} etiqueta="Sdo Condenas"
          valor={fmtMoneda(suma('saldoCondenas'))} detalle={`de ${n} prestamos`} />
        <ResumenCard clase="info-creditos" icono={cilCheckCircle} etiqueta="Saldo Real"
          valor={fmtMoneda(suma('saldoReal'))} detalle={`de ${n} prestamos`} />
      </>
    )
  }
  return null
}

/// Tarjeta de resumen con gradiente (diseño avanzado — sin emojis).
function ResumenCard({
  clase,
  icono,
  etiqueta,
  valor,
  detalle,
}: {
  clase: string
  icono: any
  etiqueta: string
  valor: string
  detalle: string
}) {
  return (
    <div className={`info-resumen-card ${clase}`}>
      <div className="info-icono">
        <CIcon icon={icono} size="xl" />
      </div>
      <div>
        <div className="info-etiqueta">{etiqueta}</div>
        <div className="info-valor">{valor}</div>
        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{detalle}</div>
      </div>
    </div>
  )
}

/// Sección del informe: header con el color de la sección + grilla CoreUI.
function SeccionInforme({
  color,
  titulo,
  total,
  columnas,
  filas,
}: {
  color: 'info' | 'success' | 'warning'
  titulo: string
  total: number
  columnas: WwColumn[]
  filas: any[]
}) {
  return (
    <CCard className="mb-3 shadow-sm border-0" style={{ overflow: 'hidden' }}>
      <CCardHeader className={`bg-${color} text-white d-flex justify-content-between align-items-center`}>
        <span className="fw-semibold text-uppercase" style={{ letterSpacing: 0.4 }}>
          {titulo}
        </span>
        <span className="fw-bold">
          Total: {fmtMoneda(total)}
        </span>
      </CCardHeader>
      <CCardBody>
        {filas.length === 0 ? (
          <div className="text-center text-secondary py-3">No hay registros en el período.</div>
        ) : (
          <WwGrid title={titulo} columns={columnas} rows={filas} onTotalChange={() => undefined} />
        )}
      </CCardBody>
    </CCard>
  )
}



/// [RGM] TAB CORTE (Informes — pedido Rafael 2026-09-13): el corte de caja del
/// día (arptcortecajadiario) con el diseño de las tarjetas de Movimientos/
/// Semanal (header azul) + Imprimir (window.print sobre .reporte-print).
function CorteTab({ empresa }: { empresa: string }) {
  const [fecha, setFecha] = useState(hoyLocal())
  const [datos, setDatos] = useState<any | null>(null)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState('')

  const generar = () => {
    setError('')
    setCargando(true)
    api.get<any>(`/cierres/corte?empresa=${empresa}&fecha=${fecha}`)
      .then(setDatos)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .reporte-print, .reporte-print * { visibility: visible !important; }
          .reporte-print { position: absolute !important; left: 0; top: 0; width: 100%; }
        }
      `}</style>
      <CCard className="mb-3 shadow-sm border-0" style={{ overflow: 'hidden' }}>
        {/* Encabezado sobrio UNIFICADO (mismo patrón de Movimientos/Semanal y
            de los demás tabs de Informes) — el título replica el reporte GX
            ARptCorteCajaDiario ("Rpt Corte Caja Diario"). */}
        <CCardHeader className="fw-semibold text-uppercase">CORTE CAJA DIARIO</CCardHeader>
        <CCardBody>
          <CRow className="g-3 align-items-end">
            <CCol md={3}>
              <CFormLabel>Fecha</CFormLabel>
              <CFormInput type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </CCol>
            <CCol md={3} className="d-flex gap-2">
              <CButton color="primary" onClick={generar} disabled={cargando}>
                {cargando ? <CSpinner size="sm" /> : <CIcon icon={cilSearch} className="me-1" />}
                Generar
              </CButton>
              {datos && (
                <CButton
                  color="success"
                  onClick={() =>
                    // [RGM] 2026-10-15 (pedido Rafael — "y el corte igual"): el
                    // Imprimir del Corte abre el PDF REAL (mismo endpoint que
                    // el Cierre Diario), no window.print (prototipo).
                    api.abrirPdf(`/cierres/corte.pdf?empresa=${empresa}&fecha=${fecha}`)
                  }
                >
                  <CIcon icon={cilPrint} className="me-1" />
                  Imprimir
                </CButton>
              )}
            </CCol>
          </CRow>
          {error && <CAlert color="danger" className="mt-3 mb-0">{error}</CAlert>}
        </CCardBody>
      </CCard>
      {datos && (
        <div className="reporte-print">
          <div className="d-flex justify-content-between align-items-center border-bottom border-2 mb-2 pb-1">
            <div className="fw-bold fs-5">Corte Caja Diario</div>
            <div className="text-end small text-body-secondary">
              <div>ARptCorteCajaDiario</div>
              <div>Rpt Corte Caja Diario</div>
            </div>
          </div>
          <div className="d-flex gap-4 small mb-2">
            <span><strong>Fecha:</strong> {String(datos.fecha ?? '').slice(0, 10)}</span>
            <span><strong>Preparado por:</strong> {datos.preparadoPor ?? '—'}</span>
            <span><strong>Empresa:</strong> {String(datos.empresaNombre ?? '').trim()}</span>
          </div>
          {(datos.cajas ?? []).map((caja: any) => (
            <div key={caja.cajaNro} className="mb-3">
              <CCard className="shadow-sm border-0 mb-2" style={{ overflow: 'hidden' }}>
                <CCardHeader className="fw-semibold">
                  Caja {caja.cajaNro} — {String(caja.cajaNombre ?? '').trim()}
                </CCardHeader>
                <CCardBody>
                  <CRow className="g-2 small">
                    <CCol md={3}><strong>Efectivo Inicial:</strong> {fmtMoneda(caja.efectivoInicial)}</CCol>
                    <CCol md={3}><strong>Otros Ingresos:</strong> {fmtMoneda(caja.otrosIngresos)}</CCol>
                    <CCol md={3}><strong>Caja Cierra Con:</strong> {fmtMoneda(caja.cajaCierraCon)}</CCol>
                  </CRow>
                </CCardBody>
              </CCard>
              {(caja.filas ?? []).length > 0 && (
                <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
                  <thead style={{ background: 'var(--cui-tertiary-bg)' }}>
                    <tr>
                      <th>Nro</th><th>RUTA</th><th>COBRADOR</th>
                      <th className="text-end">ABONOS</th><th className="text-end">PRESTAMOS</th>
                      <th className="text-end">GASTOS</th><th className="text-end">U. BRUTA</th>
                      <th className="text-end">U. NETA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(caja.filas ?? []).map((f: any) => (
                      <tr key={f.cierreDiarioNro}>
                        <td>{f.cierreDiarioNro}</td>
                        <td>{String(f.ruta ?? '').trim()}</td>
                        <td>{String(f.cobrador ?? '').trim()}</td>
                        <td className="text-end">{fmtMoneda(f.abonos)}</td>
                        <td className="text-end">{fmtMoneda(f.prestamos)}</td>
                        <td className="text-end">{fmtMoneda(f.gastos)}</td>
                        <td className="text-end">{fmtMoneda(f.utilidadBruta)}</td>
                        <td className="text-end">{fmtMoneda(f.utilidadNeta)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
          {datos.totales && (
            <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12 }}>
              <tbody>
                <tr><td className="ww-celda-total">Efectivo Inicial</td><td className="text-end">{fmtMoneda(datos.totales.efectivoInicial)}</td></tr>
                <tr><td className="ww-celda-total">Otros Ingresos</td><td className="text-end">{fmtMoneda(datos.totales.otrosIngresos)}</td></tr>
                <tr><td className="ww-celda-total">Abonos</td><td className="text-end">{fmtMoneda(datos.totales.abonos)}</td></tr>
                <tr><td className="ww-celda-total">Prestamos</td><td className="text-end">{fmtMoneda(datos.totales.prestamos)}</td></tr>
                <tr><td className="ww-celda-total">Utilidad Bruta</td><td className="text-end">{fmtMoneda(datos.totales.utilidadBruta)}</td></tr>
                <tr><td className="ww-celda-total">Gastos</td><td className="text-end">{fmtMoneda(datos.totales.gastos)}</td></tr>
                <tr><td className="ww-celda-total">Utilidad Neta</td><td className="text-end">{fmtMoneda(datos.totales.utilidadNeta)}</td></tr>
                <tr><td className="ww-celda-total">Efectivo Disponible</td><td className="text-end">{fmtMoneda(datos.totales.efectivoDisponible)}</td></tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  )
}

export default ReportesPage
